import Phaser from "phaser";
import type { VfxTweenPreset } from "./types";

type TransformLike = Phaser.GameObjects.Components.Transform &
  Phaser.GameObjects.Components.Alpha &
  Phaser.GameObjects.GameObject;

export type TweenExtras = {
  /** World-space object to lunge toward (legacy). */
  lungeToward?: Phaser.GameObjects.GameObject;
  /** Preferred: attack angle in radians for local body lunge/knock. */
  lungeAngle?: number;
};

/**
 * Apply a tween preset to a named target.
 * Special props:
 * - `lunge` / `knock`: local nudge along attack angle (always from 0,0 home
 *   when `lungeAngle` is set — avoids offset drift vs HP bar / sim position).
 */
export function playTween(
  scene: Phaser.Scene,
  preset: VfxTweenPreset,
  resolve: (key: string) => Phaser.GameObjects.GameObject | undefined,
  extras?: TweenExtras,
): void {
  const obj = resolve(preset.target);
  if (!obj) return;

  const props = { ...preset.props };
  const directional =
    ("lunge" in props ? props.lunge : undefined) ??
    ("knock" in props ? props.knock : undefined);

  if (directional !== undefined) {
    const dist = directional;
    delete props.lunge;
    delete props.knock;
    const a = obj as TransformLike;

    let dx = 0;
    let dy = 0;
    let homeX = a.x;
    let homeY = a.y;

    if (extras?.lungeAngle !== undefined) {
      // Body children must stay centered on the container (sim + HP bar).
      homeX = 0;
      homeY = 0;
      dx = Math.cos(extras.lungeAngle) * dist;
      dy = Math.sin(extras.lungeAngle) * dist;
    } else if (extras?.lungeToward) {
      const b = extras.lungeToward as TransformLike;
      const wx = b.x - a.x;
      const wy = b.y - a.y;
      const len = Math.hypot(wx, wy) || 1;
      dx = (wx / len) * dist;
      dy = (wy / len) * dist;
    } else {
      return;
    }

    scene.tweens.killTweensOf(obj);
    a.x = homeX;
    a.y = homeY;
    scene.tweens.add({
      targets: obj,
      x: homeX + dx,
      y: homeY + dy,
      duration: preset.duration,
      yoyo: preset.yoyo ?? false,
      ease: preset.ease,
      onComplete: () => {
        if (a.active) {
          a.x = homeX;
          a.y = homeY;
        }
      },
    });
    return;
  }

  scene.tweens.killTweensOf(obj);
  scene.tweens.add({
    targets: obj,
    ...props,
    duration: preset.duration,
    yoyo: preset.yoyo ?? false,
    ease: preset.ease,
    onComplete: () => {
      if (preset.destroy && obj.active) obj.destroy(true);
    },
  });
}
